import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Chip,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Skeleton
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { followUpService, FollowUpListItem, FollowUpPageResponse, FollowUpStats } from '../services/followUpService';
import CompleteFollowUpDialog from '../components/CompleteFollowUpDialog';
import ViewFollowUpDetailsDialog from '../components/ViewFollowUpDetailsDialog';

const FollowUpsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [followUps, setFollowUps] = useState<FollowUpPageResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyIncomplete, setShowOnlyIncomplete] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [stats, setStats] = useState<FollowUpStats>({ total: 0, completed: 0, pending: 0 });
  
  // Dialog states
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState<FollowUpListItem | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [viewDetailsDialogOpen, setViewDetailsDialogOpen] = useState(false);
  const [selectedFollowUpId, setSelectedFollowUpId] = useState<number | null>(null);
  
  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const fetchFollowUps = async () => {
    try {
      setLoading(true);
      const response = await followUpService.getFollowUps(
        page,
        rowsPerPage,
        searchTerm,
        !showOnlyIncomplete
      );
      setFollowUps(response);
      
      // Calculate stats - this would be better from backend but we can do it client-side
      if (response.content) {
        setStats({
          total: response.totalElements,
          completed: response.content.filter(f => f.completed).length,
          pending: response.content.filter(f => !f.completed).length
        });
      }
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
      showSnackbar('Failed to load follow-ups', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFollowUps();
  }, [page, rowsPerPage, searchTerm, showOnlyIncomplete]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleShowOnlyIncompleteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlyIncomplete(event.target.checked);
    setPage(0);
  };

  const handleCompleteClick = (followUp: FollowUpListItem) => {
    setSelectedFollowUp(followUp);
    setCompleteDialogOpen(true);
  };

  const handleViewPatientDetails = (studentId: number) => {
    navigate(`/students/${studentId}`);
  };

  const handleViewFollowUpDetails = (followUpId: number) => {
    setSelectedFollowUpId(followUpId);
    setViewDetailsDialogOpen(true);
  };

  const handleDeleteClick = (followUp: FollowUpListItem) => {
    setSelectedFollowUp(followUp);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedFollowUp) return;

    try {
      await followUpService.removeFollowUp(selectedFollowUp.id);
      showSnackbar('Follow-up removed successfully', 'success');
      fetchFollowUps();
    } catch (error) {
      console.error('Error removing follow-up:', error);
      showSnackbar('Failed to remove follow-up', 'error');
    } finally {
      setDeleteConfirmOpen(false);
      setSelectedFollowUp(null);
    }
  };

  const handleCompleteSuccess = () => {
    showSnackbar('Follow-up marked as complete', 'success');
    fetchFollowUps();
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/dashboard')}
            size="small"
          >
            Back
          </Button>
          <Typography variant="h4">
            Follow-ups List
          </Typography>
        </Box>
        <Typography variant="body1" color="textSecondary">
          Manage your patient follow-up queue
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Follow-ups
                  </Typography>
                  <Typography variant="h4">
                    {loading ? <Skeleton width={60} /> : stats.total}
                  </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Completed
                  </Typography>
                  <Typography variant="h4" sx={{ color: 'success.main' }}>
                    {loading ? <Skeleton width={60} /> : stats.completed}
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Pending
                  </Typography>
                  <Typography variant="h4" sx={{ color: 'warning.main' }}>
                    {loading ? <Skeleton width={60} /> : stats.pending}
                  </Typography>
                </Box>
                <CalendarIcon sx={{ fontSize: 40, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search by patient name..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ flexGrow: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={showOnlyIncomplete}
              onChange={handleShowOnlyIncompleteChange}
            />
          }
          label="Show only incomplete follow-ups"
        />
      </Box>

      {/* Follow-ups Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Patient Name</TableCell>
              <TableCell>School ID</TableCell>
              <TableCell>Group</TableCell>
              <TableCell>Date Added</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                </TableRow>
              ))
            ) : followUps?.content.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                    No follow-ups found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              followUps?.content.map((followUp) => (
                <TableRow key={followUp.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      <Typography variant="body2">{followUp.patientName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SchoolIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      <Typography variant="body2">{followUp.schoolId}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{followUp.group}</TableCell>
                  <TableCell>{formatDate(followUp.followupDate)}</TableCell>
                  <TableCell>
                    {followUp.completed ? (
                      <Chip
                        label="Completed"
                        color="success"
                        size="small"
                        icon={<CheckCircleIcon />}
                      />
                    ) : (
                      <Chip
                        label="Pending"
                        color="warning"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        maxWidth: 200, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap' 
                      }}
                      title={followUp.notes}
                    >
                      {followUp.notes || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      {!followUp.completed && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleCompleteClick(followUp)}
                          startIcon={<CheckCircleIcon />}
                        >
                          Complete
                        </Button>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (followUp.completed) {
                            handleViewFollowUpDetails(followUp.id);
                          } else {
                            handleViewPatientDetails(followUp.studentId);
                          }
                        }}
                        title={followUp.completed ? "View Follow-up Details" : "View Patient Details"}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(followUp)}
                        title="Remove"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {followUps && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={followUps.totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </TableContainer>

      {/* Complete Follow-up Dialog */}
      {selectedFollowUp && (
        <CompleteFollowUpDialog
          open={completeDialogOpen}
          onClose={() => {
            setCompleteDialogOpen(false);
            setSelectedFollowUp(null);
          }}
          followUp={selectedFollowUp}
          onSuccess={handleCompleteSuccess}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Remove Follow-up?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove the follow-up for {selectedFollowUp?.patientName}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Follow-up Details Dialog */}
      {selectedFollowUpId && (
        <ViewFollowUpDetailsDialog
          open={viewDetailsDialogOpen}
          onClose={() => {
            setViewDetailsDialogOpen(false);
            setSelectedFollowUpId(null);
          }}
          followUpId={selectedFollowUpId}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FollowUpsListPage;